import useSWR from 'swr';
import request from './request';

/*
  Axios call methods
*/

export const getEntries = async (companyId) => {
  return request({
    url: `JournalEntries/Company/${companyId}`,
    method: 'GET',
  });
};

export const getEntriesBankBalance = async (companyId) => {
  return request({
    url: `JournalEntries/Company/${companyId}/BanksBalance`,
    method: 'GET',
  });
};

export const getEntriesFiltered = async (companyId, start, end) => {
  return request({
    url: `JournalEntries/Company/${companyId}/Filtered?startDate=${start}T00%3A00%3A00Z&endDate=${end}T23%3A59%3A59Z`,
    method: 'GET',
  });
};

export const getEntriesFilteredByDate = async (
  companyId,
  start,
  end,
  type,
  includeReconciliationDetail = false
) => {
  return request({
    url: `JournalEntries/Company/${companyId}/FilteredByDate?startDate=${start}T00%3A00%3A00Z&endDate=${end}T23%3A59%3A59Z&entryType=${type}${includeReconciliationDetail ? '&includeReconciliationDetail=true' : ''}`,
    method: 'GET',
  });
};

export const getEntry = async (entryId) => {
  return request({
    url: `JournalEntries/${entryId}`,
    method: 'GET',
  });
};

export const putEntry = async (entry) => {
  return request({
    url: `JournalEntries/${entry.id}`,
    method: 'POST',
    data: entry,
  });
};

export const putEntryTransfer = async (entry) => {
  return request({
    url: `JournalEntries/UpdateTransfer/${entry.id}`,
    method: 'POST',
    data: entry,
  });
};

export const putManyEntry = async (entryList) => {
  return request({
    url: `JournalEntries/UpdateMany`,
    method: 'POST',
    data: entryList,
  });
};

export const postEntry = async (entry) => {
  return request({
    url: `JournalEntries/`,
    method: 'POST',
    data: entry,
  });
};

export const postManyEntries = async (entries) => {
  return request({
    url: `JournalEntries/CreateMany`,
    method: 'POST',
    data: entries,
  });
};

export const postManyEntriesWithReconciliation = async (
  entriesData,
  reconciliationPairs,
  options = {}
) => {
  return request({
    url: `JournalEntries/CreateManyWithReconciliation`,
    method: 'POST',
    data: {
      entriesData,
      reconciliationPairs,
      options,
    },
  });
};

export const deleteEntry = async (entry) => {
  return request({
    url: `JournalEntries/${entry}`,
    method: 'DELETE',
  });
};

export const deleteManyEntries = async (entryList) => {
  return request({
    url: `JournalEntries/DeleteMany`,
    method: 'DELETE',
    data: entryList,
  });
};

/*
  useQuery - SWR methods
*/

export const useQueryEntries = (companyId, options) => {
  const key = companyId ? `entries-${companyId}` : null;
  const { data, error, mutate, isValidating } = useSWR(
    key,
    companyId ? () => getEntries(companyId) : null,
    options ?? null
  );

  return {
    data,
    isLoading: !error && !data,
    isError: error,
    mutate,
    isValidating,
  };
};

export const useQueryEntriesBankBalance = (companyId, options) => {
  const key = 'entriesBankBalance';
  const { data, error, mutate, isValidating } = useSWR(
    () => key,
    companyId ? () => getEntriesBankBalance(companyId) : null,
    options ?? null
  );

  return {
    data,
    isLoading: !error && !data,
    isError: error,
    mutate,
    isValidating,
  };
};

export const useQueryEntriesFiltered = (companyId, start, end, type, options) => {
  const key = 'entriesFiltered';
  const { data, error, mutate, isValidating } = useSWR(
    () => key,
    companyId ? () => getEntriesFiltered(companyId, start, end, type) : null,
    options ?? null
  );

  return {
    data,
    isLoading: !error && !data,
    isError: error,
    mutate,
    isValidating,
  };
};

export const useQueryEntriesFilteredByDate = (companyId, start, end, type, options) => {
  const key = 'entriesFilteredByDate';
  const { data, error, mutate, isValidating } = useSWR(
    () => key,
    companyId ? () => getEntriesFilteredByDate(companyId, start, end, type) : null,
    options ?? null
  );

  return {
    data,
    isLoading: !error && !data,
    isError: error,
    mutate,
    isValidating,
  };
};

export const useQueryEntriesFilteredByYear = (companyId, start, end, type, options) => {
  const key = companyId ? `entriesFilteredByYear-${companyId}-${start}-${end}-${type}` : null;
  const { data, error, mutate, isValidating } = useSWR(
    key,
    companyId ? () => getEntriesFilteredByDate(companyId, start, end, type) : null,
    options ?? null
  );

  return {
    data,
    isLoading: !error && !data,
    isError: error,
    mutate,
    isValidating,
  };
};

export const useQueryEntriesCalendar = (companyId, start, end, type, options) => {
  const key = 'entriesCalendar';
  const { data, error, mutate, isValidating } = useSWR(
    () => key,
    companyId ? () => getEntriesFilteredByDate(companyId, start, end, type) : null,
    options ?? null
  );

  return {
    data,
    isLoading: !error && !data,
    isError: error,
    mutate,
    isValidating,
  };
};

export const useQueryEntriesReconciliation = (companyId, start, end, type, options) => {
  const key = 'entriesReconciliation';

  // Validate date format
  const isValidDate = (dateString) => {
    return (
      dateString &&
      typeof dateString === 'string' &&
      /^\d{4}-\d{2}-\d{2}$/.test(dateString) &&
      !dateString.includes('-') === false
    ); // Basic sanity check
  };

  // Calculate fallback dates if not provided or invalid
  const getFallbackDates = () => {
    const now = new Date();
    const threeMonthsAgo = new Date();
    threeMonthsAgo.setMonth(now.getMonth() - 3);
    return {
      fallbackStart: threeMonthsAgo.toISOString().substring(0, 10),
      fallbackEnd: now.toISOString().substring(0, 10),
    };
  };

  // Use fallback dates if dates are missing or invalid (like UUIDs)
  const { fallbackStart, fallbackEnd } = getFallbackDates();
  const effectiveStart = isValidDate(start) ? start : fallbackStart;
  const effectiveEnd = isValidDate(end) ? end : fallbackEnd;

  const { data, error, mutate, isValidating } = useSWR(
    () =>
      companyId ? `${key}_${effectiveStart}_${effectiveEnd}_${type || 'dateVencimento'}` : null,
    () =>
      getEntriesFilteredByDate(
        companyId,
        effectiveStart,
        effectiveEnd,
        type || 'dateVencimento',
        true // reconciliation screen needs the full reconciliationData (matched bank transactions/entries)
      ),
    options ?? null
  );

  return {
    data,
    isLoading: !error && !data,
    isError: error,
    mutate,
    isValidating,
  };
};

export const useQueryEntry = (entryId) => {
  const key = 'entry';
  const { data, error, mutate } = useSWR(() => key, entryId ? () => getEntry(entryId) : null);

  return {
    data,
    isLoading: !error && !data,
    isError: error,
    mutate,
  };
};
